import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { twMerge } from 'tailwind-merge';
import Icon from 'flow/components/ui-components/Icon';
import TicketAssigneeItem from 'flow/components/tickets/TicketAssigneeItem';
import { selectAssignee } from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { TicketKey } from 'flow/types/EntityKeys';

import AssigneeChipAvatar from './AssigneeChipAvatar';
import EditableAssigneeChip from './EditableAssigneeChip';
import { CHIP_BASE_CLASSES, getChipBackplateClass } from './chip-classes';

type Props = {
    ticketKey: TicketKey;
    isSelected: boolean;
    muted?: boolean;
};

const AssigneeChip = ({ ticketKey, isSelected, muted }: Props) => {
    const assigneeKey = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );
    const canAssign = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'assign'),
    );

    const assigneeIsAssigned = assigneeKey !== 'unassigned';

    const chipClassName = twMerge(
        CHIP_BASE_CLASSES,
        getChipBackplateClass(isSelected),
        assigneeIsAssigned ? null : 'text-gray-500',
        // Closed tickets: mid-gray label and a faded avatar.
        muted ? 'text-gray-500' : null,
    );

    const content = (
        <>
            {assigneeIsAssigned ? (
                <AssigneeChipAvatar assigneeKey={assigneeKey} muted={muted} />
            ) : (
                <Icon iconKey="pendingOutlined" size="1rem" />
            )}
            <TicketAssigneeItem
                ticketKey={ticketKey}
                abbreviateLastName={true}
            />
        </>
    );

    if (!canAssign) {
        return <div className={chipClassName}>{content}</div>;
    }

    return (
        <EditableAssigneeChip
            ticketKey={ticketKey}
            chipClassName={chipClassName}
        >
            {content}
        </EditableAssigneeChip>
    );
};

export default React.memo(AssigneeChip);
